import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ClipboardCheck,
  FileEdit,
  Trash2,
  Plus,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  hasChecklistTemplate,
  getChecklistTemplate,
} from '@/lib/checklists/registry';
import {
  filtrarPorTipoRevision,
  type TipoRevision,
} from '@/lib/checklists/types';

interface SistemaRevisionMeta {
  ultimoFinalizado?: { fecha: string; tipo_revision: string; periodo: string | null };
  borrador?: { id: string; tipo_revision: string; fecha: string; periodo: string | null; progreso: number };
}

const TIPO_REVISION_LETTER: Record<string, 'T' | 'S' | 'A'> = {
  trimestral: 'T',
  semestral: 'S',
  anual: 'A',
};

const ESTADOS_FINALIZADOS = ['finalizado', 'completado', 'finalizada'];

const formatPeriodo = (periodo?: string | null, fallbackIso?: string | null): string => {
  if (periodo && /^(\d{4})-(\d{2})$/.test(periodo)) {
    return `${periodo.slice(5)}/${periodo.slice(0, 4)}`;
  }
  if (!fallbackIso) return '';
  const d = new Date(fallbackIso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const totalPreguntasTemplate = (tipoSistema: string, tipo: TipoRevision): number => {
  const t = getChecklistTemplate(tipoSistema);
  if (!t) return 0;
  return t.protocolos.reduce(
    (acc, p) => acc + filtrarPorTipoRevision(p.preguntas, tipo).length,
    0
  );
};

interface Props {
  sistemaId: string;
  sistemaTipo: string;
  clienteId: string;
  clienteNombre?: string;
}

export const SistemaRevisionActions: React.FC<Props> = ({
  sistemaId,
  sistemaTipo,
  clienteId,
  clienteNombre,
}) => {
  const { perfil } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [meta, setMeta] = useState<SistemaRevisionMeta>({});
  const [confirmNuevo, setConfirmNuevo] = useState<boolean>(false);

  const irARevision = useCallback((checklistId?: string) => {
    const params = new URLSearchParams({
      sistemaId,
      sistemaTipo,
      clienteId,
      clienteNombre: clienteNombre || '',
      returnTo: location.pathname + location.search,
    });
    if (checklistId) params.set('checklistId', checklistId);
    navigate(`/checklist-revision?${params.toString()}`);
  }, [navigate, sistemaId, sistemaTipo, clienteId, clienteNombre, location]);

  const cargar = useCallback(async () => {
    if (!clienteId || !sistemaId) return;
    const { data } = await (supabase as any)
      .from('checklists_seguridad')
      .select('id, sistema_id, tecnico_id, estado, tipo_revision, fecha, updated_at, modo, periodo')
      .eq('cliente_id', clienteId)
      .eq('sistema_id', sistemaId)
      .eq('archivado', false)
      .neq('modo', 'elementos')
      .order('updated_at', { ascending: false });

    const next: SistemaRevisionMeta = {};
    let borradorChecklist: { id: string; tipo_revision: string } | null = null;

    (data || []).forEach((row: any) => {
      const fecha = row.updated_at || row.fecha;
      const periodo = (row.periodo as string | null) ?? null;
      const esBorrador = row.estado === 'en_progreso';
      const esFinalizado = ESTADOS_FINALIZADOS.includes(row.estado);

      if (esFinalizado && !next.ultimoFinalizado) {
        next.ultimoFinalizado = { fecha, tipo_revision: row.tipo_revision, periodo };
      }
      if (esBorrador && !next.borrador && row.tecnico_id === perfil?.user_id) {
        next.borrador = { id: row.id, tipo_revision: row.tipo_revision, fecha, periodo, progreso: 0 };
        borradorChecklist = { id: row.id, tipo_revision: row.tipo_revision };
      }
    });

    if (borradorChecklist) {
      const { data: resp } = await supabase
        .from('checklist_respuestas')
        .select('pregunta_id, respuesta')
        .eq('checklist_id', borradorChecklist.id);
      const respondidas = new Set<string>();
      (resp || []).forEach((r: any) => { if (r.respuesta) respondidas.add(r.pregunta_id); });
      const total = totalPreguntasTemplate(sistemaTipo, (borradorChecklist.tipo_revision || 'trimestral') as TipoRevision);
      const progreso = total > 0 ? Math.min(100, Math.round((respondidas.size / total) * 100)) : 0;
      if (next.borrador) next.borrador.progreso = progreso;
    }

    setMeta(next);
  }, [clienteId, sistemaId, sistemaTipo, perfil?.user_id]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleEliminarBorrador = async (checklistId: string) => {
    try {
      await supabase.from('checklist_respuestas').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_elementos_probados').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_cabeceras').delete().eq('checklist_id', checklistId);
      await supabase.from('checklists_seguridad').delete().eq('id', checklistId);
      await cargar();
    } catch (e) {
      console.error(e);
    }
  };

  const tieneTemplate = hasChecklistTemplate(sistemaTipo);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-sm font-medium text-foreground">Revisiones del sistema</div>

      {tieneTemplate ? (
        <div className="space-y-2">
          {(meta.ultimoFinalizado || meta.borrador) && (
            <div className="flex flex-wrap gap-2">
              {meta.ultimoFinalizado && (
                <Badge variant="outline" className="gap-1 border-green-500/40 text-green-700 dark:text-green-400">
                  <ClipboardCheck className="h-3 w-3" />
                  Última revisión: {formatPeriodo(meta.ultimoFinalizado.periodo, meta.ultimoFinalizado.fecha)}
                  <span className="ml-1 font-bold">
                    {TIPO_REVISION_LETTER[meta.ultimoFinalizado.tipo_revision] || '?'}
                  </span>
                </Badge>
              )}
              {meta.borrador && (
                <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500 border-amber-600 shadow-sm">
                  <FileEdit className="h-3 w-3" />
                  Borrador {TIPO_REVISION_LETTER[meta.borrador.tipo_revision] || '?'}: {formatPeriodo(meta.borrador.periodo, meta.borrador.fecha)} · {meta.borrador.progreso}%
                  <button
                    type="button"
                    className="ml-1 -mr-1 rounded hover:bg-white/20 p-0.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('¿Eliminar borrador de revisión?')) {
                        handleEliminarBorrador(meta.borrador!.id);
                      }
                    }}
                    aria-label="Eliminar borrador"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant={meta.borrador ? 'default' : 'outline'}
              size="sm"
              className="flex-1 gap-2"
              onClick={() => irARevision(meta.borrador?.id)}
            >
              {meta.borrador ? (
                <>
                  <FileEdit className="h-4 w-4" />
                  Continuar revisión
                </>
              ) : (
                <>
                  <ClipboardCheck className="h-4 w-4" />
                  Empezar revisión
                </>
              )}
            </Button>
            {meta.borrador && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => setConfirmNuevo(true)}
                title="Empezar nueva revisión (eliminará el borrador)"
              >
                <Plus className="h-4 w-4" />
                Nueva
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          No hay plantilla de revisión disponible para este tipo de sistema.
        </p>
      )}

      <AlertDialog open={confirmNuevo} onOpenChange={(o) => { if (!o) setConfirmNuevo(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Empezar nueva revisión?</AlertDialogTitle>
            <AlertDialogDescription>
              Existe un borrador en curso. Si continúas, <strong>el borrador anterior será eliminado</strong> de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (meta.borrador) {
                  await handleEliminarBorrador(meta.borrador.id);
                  setConfirmNuevo(false);
                  irARevision(undefined);
                }
              }}
            >
              Eliminar y empezar nueva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
